import { useState, useRef, useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { IdentificationIcon } from '@heroicons/react/24/outline';
import { MoreVertical } from 'react-feather';
import { Menu, MenuSize } from '@/shared/components/Menu';
import plLabels from '@/epics/facilityManagement/constants/plLabels.json';

interface DropdownPosition {
  top: number;
  right: number;
}

interface ListCardProps {
  name: string;
  entityId: string;
  icon?: ReactNode;
  imageUrl?: string | null;
  onEdit?: () => void;
  editDisabled?: boolean;
  editDisabledTooltip?: string | undefined;
  index?: number;
  errorMessage?: string | undefined;
}

export const ListCard = ({
  name,
  entityId,
  icon,
  imageUrl,
  onEdit,
  editDisabled = false,
  editDisabledTooltip,
  index = 0,
  errorMessage,
}: ListCardProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<DropdownPosition>({
    top: 0,
    right: 0,
  });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const openMenu = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }
    setIsMenuOpen(true);
  };

  useEffect(() => {
    if (!isMenuOpen) {
      return;
    }
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };
    const handleScroll = () => setIsMenuOpen(false);
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [isMenuOpen]);

  return (
    <div>
      <div
        className={`bg-white border rounded-[12px] p-4 flex items-center gap-4 ${errorMessage ? 'border-[#B3261E]' : 'border-[#DCDDE0]'}`}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={name}
            className="w-10 h-10 rounded-full object-cover shrink-0"
          />
        ) : icon ? (
          <div className="bg-neutral-90 rounded-full p-2 shrink-0">{icon}</div>
        ) : null}

        <div className="flex flex-col gap-2 flex-1 min-w-0">
          <p className="text-bodyMedium text-neutral-10 wrap-break-word">
            {name}
          </p>
          <div className="flex items-center gap-1">
            <IdentificationIcon className="w-4 h-4 text-outline shrink-0" />
            <span className="text-bodySmall text-outline">{entityId}</span>
          </div>
        </div>

        <button
          ref={buttonRef}
          type="button"
          className="w-10 h-10 flex items-center justify-center rounded-full cursor-pointer hover:bg-[#474A4C]/[0.08] shrink-0"
          data-testid={`equipment-menu-button-${index}`}
          aria-label="Więcej opcji"
          onClick={() => (isMenuOpen ? setIsMenuOpen(false) : openMenu())}
        >
          <MoreVertical size={20} className="text-on-surface-variant" />
        </button>

        {isMenuOpen &&
          createPortal(
            <div
              ref={dropdownRef}
              className="fixed z-[9999]"
              style={{ top: dropdownPos.top, right: dropdownPos.right }}
            >
              <Menu
                items={[
                  {
                    id: 'edit',
                    label: plLabels.common.edit,
                    disabled: editDisabled,
                    ...(editDisabled && {
                      disabledTooltip:
                        editDisabledTooltip ?? plLabels.common.noPermission,
                      disabledTooltipSide: 'left' as const,
                    }),
                  },
                ]}
                size={MenuSize.Small}
                showLeadingIcons={false}
                showTrailingArrows={false}
                toolTip={false}
                onItemClick={() => {
                  setIsMenuOpen(false);
                  onEdit?.();
                }}
              />
            </div>,
            document.body
          )}
      </div>
      {errorMessage && (
        <p className="text-xs text-[#B3261E] leading-4 tracking-[0.4px] px-1 mt-1">
          {errorMessage}
        </p>
      )}
    </div>
  );
};
